/*----------------------------------
- Navigation Menu
----------------------------------*/

import ProLockedNavItem from '@/client/components/trial/ProLockedNavItem';
import React from 'react';

import { Link } from '@client/components';
import useContext from '@/client/context';
import SidebarNavigationLink from '@/client/components/shared/SidebarNavigationLink';
import { SidebarRailItem, SidebarRailDivider } from '@/client/components/shared/SidebarRail';
import Button from '@/client/components/ui/Button';

import {
    ChevronDown,
    Rocket,
    Briefcase,
    Building2,
    Wallet,
    ChartColumn,
    FilePlus2,
    UserRoundSearch,
    Handshake,
    Crosshair,
    GraduationCap,
    Puzzle,
    Gem,
    Link2,
    Send,
    Megaphone,
    FileSignature,
    Package,
    Calculator,
    Layers,
    LineChart,
    Network,
    IdCard,
    Rss,
    Download,
    Radar,
    ClipboardList,
    Workflow,
    UsersRound,
    ScanSearch,
    LifeBuoy,
    Compass,
    Target,
    Settings2,
    UserCog,
    Plug,
    Sparkles,
    LogOut,
    Lock,
} from 'lucide-preact';

interface NavigationItemProps {
    id: string;
    label: string;
    icon: any;
    type?: 'single' | 'parent';
    path?: string;
    disable?: boolean;
    sidebarCollapsed: boolean;
    isExpanded?: boolean;
    onToggle?: () => void;
    isMobile?: boolean;
    onNavigate?: () => void;
    registerLabel: (id: string, label: string) => void;
    currentPathname?: string;
    isFreeFeature?: (itemId: string) => boolean;
    userType?: string;
    redirectToFirstChild?: boolean;
    children?: React.ReactNode;
    status?: 'todo' | 'wip';
    onRequestExpand?: () => void;
}

interface NavigationItemChildProps {
    id: string;
    label: string;
    icon: any;
    path?: string;
    disable?: boolean;
    parentId?: string;
    isMobile?: boolean;
    onNavigate?: () => void;
    isFreeFeature?: (itemId: string) => boolean;
    currentPathname?: string;
    status?: 'todo' | 'wip';
}

export interface NavigationMenuProps {
    user: any;
    sidebarCollapsed: boolean;
    isMenuExpanded: (id: string) => boolean;
    onToggleMenu: (id: string) => void;
    registerNavigationItem: (id: string, label: string) => void;
    isFreeFeature?: (id: string) => boolean;
    variant?: 'desktop' | 'mobile';
    onNavigate?: () => void;
    currentPathname?: string;
    onLogout?: () => void;
    onRequestExpand?: () => void;
}

/**
 * Compute the target URL for a child menu item based on its parent and explicit path.
 */
const resolveChildPath = (parentId: string | undefined, childId: string, explicitPath?: string): string => {
    if (explicitPath) {
        return explicitPath;
    }

    if (parentId === 'affiliation' && (childId === 'referal' || childId === 'marketing')) {
        return `/dashboard/affiliation/${childId}`;
    }

    if (childId.startsWith('admin-')) {
        return `/admin/${childId.replace('admin-', '')}`;
    }

    return `/dashboard/${childId}`;
};

const isNavigationPathActive = (currentPathname: string, destination?: string): boolean => {
    if (!destination) {
        return false;
    }

    const current = currentPathname.split('?')[0] || '/';
    const target = destination.split('?')[0] || '/';

    if (target === '/' || target === '/dashboard') {
        return current === target;
    }

    return current === target || current.startsWith(`${target}/`);
};

const getFallbackPathname = (): string =>
    typeof window === 'undefined' ? '' : window.location.pathname;

/**
 * Define default expanded menu ids according to the user permissions.
 */
export const buildDefaultExpandedMenus = (user: any): string[] => {
    const defaults = ['sales', 'sourcing', 'settings'];

    if (user?.roles?.includes('ADMIN')) {
        return ['cp-management', ...defaults];
    }

    if (user?.roles?.includes('AMBASSADOR')) {
        return ['ambassadors', ...defaults];
    }

    return defaults;
};

/**
 * Enumerate every parent menu id that can be rendered for a given user.
 */
export const getParentMenuIds = (user: any): string[] => {
    const baseParents = ['sales', 'sourcing', 'settings'];

    if (user?.roles?.includes('ADMIN')) {
        return ['cp-management', ...baseParents];
    }

    if (user?.roles?.includes('AMBASSADOR')) {
        return ['ambassadors', ...baseParents];
    }

    return baseParents;
};

/**
 * Render a child entry for a navigation parent.
 */
const NavigationItemChild: React.FC<NavigationItemChildProps> = ({
    id,
    label,
    icon: ChildIcon,
    path,
    disable = false,
    parentId,
    isMobile = false,
    onNavigate,
    isFreeFeature,
    currentPathname,
    status,
}) => {
    const { user } = useContext();

    if (status !== undefined && !user?.roles?.includes('ADMIN')) return null;

    const destination = resolveChildPath(parentId, id, path);
    const active = isNavigationPathActive(currentPathname || getFallbackPathname(), destination);
    const dataTestId = parentId ? `nav-child-${parentId}-${id}` : `nav-child-${id}`;
    const sharedChildClasses = 'group flex items-center gap-3 border-l-2 px-3 py-2 rounded-lg text-sm font-medium tracking-wide transition-all duration-200 active:scale-[0.98]';
    const enabledChildClasses = active
        ? 'border-primary-500 bg-primary-100 text-primary-700 font-semibold shadow-sm'
        : 'border-transparent text-slate-900 hover:border-primary-200 hover:bg-primary-50/70 hover:text-primary-700';
    const disabledChildClasses = 'border-transparent text-slate-400 opacity-50 cursor-not-allowed';
    const featureGuard = isFreeFeature ?? (() => true);
    const isFree = featureGuard(id);

    if (disable) {
        return (
            <div
                className={`${sharedChildClasses} ${disabledChildClasses}`}
                aria-disabled="true"
                data-testid={dataTestId}
            >
                <ChildIcon size={18} strokeWidth={1.5} className="text-slate-400" />
                <span className="flex-1 leading-5 tracking-wide">{label}</span>
                {/*!isFree && userType === 'cp_platform_user' && (
                    <Lock size={14} className="text-orange-500" />
                )*/}
            </div>
        );
    }

    return (
        <Link
            to={destination}
            className={`${sharedChildClasses} ${enabledChildClasses}`}
            onClick={isMobile ? onNavigate : undefined}
            data-testid={dataTestId}
            aria-current={active ? 'page' : undefined}
            data-active={active ? 'true' : 'false'}
        >
            <ChildIcon size={18} strokeWidth={1.75} className={`shrink-0 transition-colors duration-200 ${active ? 'text-primary-600' : 'text-slate-900 group-hover:text-primary-500'}`} />
            <span className="flex-1 truncate tracking-wide">{label}</span>

            {/* Show preview orange dot if isPreview is true */}
            {status !== undefined && (
                <span
                    title={status}
                    aria-label={status}
                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${status === 'todo' ? 'bg-red-500' : 'bg-amber-400'}`}
                />
            )}
            {/*!isFree && userType === 'cp_platform_user' && (
                <Lock size={14} className="text-orange-500" />
            )*/}
        </Link>
    );
};

/**
 * Render a navigation entry, either a single route or a parent with nested children.
 */
const NavigationItem: React.FC<NavigationItemProps> & { Child: React.FC<NavigationItemChildProps> } = ({
    id,
    label,
    icon: Icon,
    type = 'single',
    path,
    disable = false,
    sidebarCollapsed,
    isMobile = false,
    onNavigate,
    registerLabel,
    currentPathname,
    isFreeFeature,
    userType,
    children,
    status,
    plan = false,
    onRequestExpand,
}) => {
    const { user } = useContext();
    const [isOpen, setIsOpen] = React.useState(true);

    if (plan && !user?.plan) return null;

    if (status && !user?.roles?.includes('ADMIN')) return null;

    React.useEffect(() => {
        registerLabel(id, label);
    }, [id, label, registerLabel]);

    if (type === 'single') {
        const linkPath = path ?? (id === 'dashboard' ? '/dashboard' : `/dashboard/${id}`);
        const active = isNavigationPathActive(currentPathname || getFallbackPathname(), linkPath);
        if (id === 'admin-start-here') {
            if (sidebarCollapsed && !isMobile) {
                return (
                    <SidebarRailItem
                        label={label}
                        icon={<Icon size={18} strokeWidth={1.75} />}
                        href={linkPath}
                        active={active}
                        tone="accent"
                        dataTestId={`nav-item-${id}`}
                    />
                );
            }

            return (
                <div className="mb-2 rounded-2xl border border-orange-100 bg-orange-50/40 p-1.5 shadow-sm shadow-orange-100/60 transition-all duration-300 hover:-translate-y-0.5 hover:border-orange-200 hover:shadow-md hover:shadow-orange-100/80">
                    <SidebarNavigationLink
                        label={label}
                        href={linkPath}
                        icon={Icon}
                        dataTestId={`nav-item-${id}`}
                        disabled={disable}
                        active={active}
                        collapsed={sidebarCollapsed}
                        isMobile={isMobile}
                        onNavigate={onNavigate}
                    />
                </div>
            );
        }

        return (
            <SidebarNavigationLink
                label={label}
                href={linkPath}
                icon={Icon}
                dataTestId={`nav-item-${id}`}
                disabled={disable}
                active={active}
                collapsed={sidebarCollapsed}
                isMobile={isMobile}
                onNavigate={onNavigate}
            />
        );
    }

    const childElements = React.Children.toArray(children).reduce<React.ReactElement<NavigationItemChildProps>[]>(
        (acc, child) => {
            if (React.isValidElement<NavigationItemChildProps>(child)) {
                acc.push(child);
            }
            return acc;
        },
        [],
    );

    const renderedChildren = childElements.map((child) =>
        React.cloneElement(child, { parentId: id, isMobile, onNavigate, isFreeFeature, userType, currentPathname }),
    );

    if (sidebarCollapsed && !isMobile) {
        if (renderedChildren.length === 0) return null;

        // No labels on the rail, so the group lights up whenever one of its pages is the current one.
        const hasActiveChild = childElements.some((child) =>
            isNavigationPathActive(
                currentPathname || getFallbackPathname(),
                resolveChildPath(id, child.props.id, child.props.path),
            ),
        );

        return (
            <>
                <SidebarRailDivider />
                <SidebarRailItem
                    label={label}
                    icon={<Icon size={18} strokeWidth={1.75} />}
                    active={hasActiveChild}
                    onClick={() => {
                        setIsOpen(true);
                        onRequestExpand?.();
                    }}
                    dataTestId={`nav-parent-${id}`}
                />
            </>
        );
    }

    if (renderedChildren.length === 0) {
        return null;
    }

    return (
        <div className="flex w-full flex-col border-t border-slate-300 pt-3 mt-3 first:mt-0 first:border-t-0 first:pt-0">
            <button
                type="button"
                onClick={() => setIsOpen((previous) => !previous)}
                className="group flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left transition-all duration-200 hover:bg-primary-50/70 active:scale-[0.98] cursor-pointer"
                data-testid={`nav-parent-${id}`}
                aria-expanded={isOpen}
            >
                <span className="flex items-center gap-2 text-[11.5px] font-bold uppercase tracking-widest text-primary-400 transition-colors duration-200 group-hover:text-primary-600">
                    <Icon size={16} strokeWidth={1.75} className="text-primary-300 transition-colors duration-200 group-hover:text-primary-500" />
                    {label}
                </span>
                <ChevronDown
                    className={`h-4 w-4 shrink-0 cursor-pointer text-primary-300 transition-transform duration-300 ease-in-out group-hover:text-primary-500 ${isOpen ? '' : '-rotate-90'}`}
                    strokeWidth={2}
                />
            </button>

            <div
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
            >
                <div className="flex min-h-0 flex-col gap-0.5 overflow-hidden pt-1.5">{renderedChildren}</div>
            </div>
        </div>
    );
};

NavigationItem.Child = NavigationItemChild;

/**
 * Display the navigation structure for both desktop and mobile contexts.
 */
const NavigationMenu: React.FC<NavigationMenuProps> = ({
    user,
    sidebarCollapsed,
    isMenuExpanded,
    onToggleMenu,
    registerNavigationItem,
    variant = 'desktop',
    onNavigate,
    isFreeFeature,
    currentPathname,
    onLogout,
    onRequestExpand,
}) => {
    const isMobile = variant === 'mobile';
    const effectiveSidebarCollapsed = isMobile ? false : sidebarCollapsed;
    const handleNavigate = isMobile ? onNavigate : undefined;
    const registerItem = registerNavigationItem;
    const menuExpanded = isMenuExpanded;
    const toggleMenu = onToggleMenu;
    const featureGuard = isFreeFeature ?? (() => true);

    const isAdmin = user?.roles?.includes('ADMIN');
    const isCSM = isAdmin || user?.roles?.includes('CSM');
    const haAmbassadorTools = isAdmin || Object.values(user?.features?.ambassador ?? {}).some(Boolean);
    const hasBizdevTools = isAdmin || Object.values(user?.features?.bizdev ?? {}).some(Boolean);
    const hasSourcingTools =
        isAdmin ||
        user?.features?.sourcing?.activeRoles ||
        user?.features?.sourcing?.search ||
        user?.features?.sourcing?.campaigns;

    React.useEffect(() => {
        registerItem('invite', 'Refer a Friend');
    }, [registerItem]);

    const navPadding = effectiveSidebarCollapsed && !isMobile ? 'p-2' : 'p-4';
    const menuScrollPadding = effectiveSidebarCollapsed && !isMobile ? '' : 'pr-1';

    return (
        <nav className={`flex min-h-0 flex-1 flex-col overflow-hidden ${navPadding}`}>
            <div
                className={`min-h-0 flex-1 space-y-1 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:var(--color-slate-200)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-200 ${menuScrollPadding}`}
            >
                <NavigationItem
                    id="admin-start-here"
                    label="Start Here"
                    icon={Rocket}
                    type="single"
                    path="/"
                    sidebarCollapsed={effectiveSidebarCollapsed}
                    onRequestExpand={onRequestExpand}
                    registerLabel={registerItem}
                    userType={user?.type}
                    isMobile={isMobile}
                    onNavigate={handleNavigate}
                />

            {user?.roles?.includes('ADMIN') && (
                <NavigationItem
                    id="cp-management"
                    label="CP management"
                    icon={Building2}
                    type="parent"
                    sidebarCollapsed={effectiveSidebarCollapsed}
                    onRequestExpand={onRequestExpand}
                    isExpanded={menuExpanded('cp-management')}
                    onToggle={() => toggleMenu('cp-management')}
                    registerLabel={registerItem}
                    userType={user?.type}
                    isMobile={isMobile}
                    onNavigate={handleNavigate}
                >
                    {/* Hidden for now — not deleted, easy to bring back later */}
                    {false && (
                        <NavigationItem.Child
                            id="admin-add-mission"
                            label="Add a Mission"
                            icon={FilePlus2}
                            path="/dashboard/crm-mission?create=1"
                        />
                    )}
                    <NavigationItem.Child
                        id="admin-headhunters"
                        label="Headhunters"
                        icon={UserRoundSearch}
                        path="/admin/headhunters"
                    />
                    <NavigationItem.Child
                        id="admin-talents"
                        label="Talents"
                        icon={GraduationCap}
                        path="/csm/talents"
                    />
                    <NavigationItem.Child
                        id="admin-companies"
                        label="Companies"
                        icon={Briefcase}
                        path="/admin/companies"
                    />
                    <NavigationItem.Child
                        id="admin-payment"
                        label="Payments"
                        icon={Wallet}
                        path="/admin/payment"
                    />
                    {/* Hidden for now — not deleted, easy to bring back later */}
                    {false && (
                        <NavigationItem.Child
                            id="admin-partners"
                            label="Partners"
                            icon={Handshake}
                            path="/admin/partners"
                        />
                    )}
                    <NavigationItem.Child
                        id="admin-bd-partner-applications"
                        label="BD partner applications"
                        icon={Handshake}
                        path="/admin/bd-partner-applications"
                    />
                    <NavigationItem.Child
                        id="admin-outreach-report"
                        label="Campaign Operations"
                        icon={ChartColumn}
                        path="/admin/outreach-report"
                    />
                    {/* Hidden for now — not deleted, easy to bring back later */}
                    {false && (
                        <NavigationItem.Child
                            id="admin-review-missions"
                            label="Missions"
                            icon={Crosshair}
                            path="/bizdev/missions"
                        />
                    )}
                </NavigationItem>
            )}

            {haAmbassadorTools && (
                <NavigationItem
                    id="ambassadors"
                    label="Ambassadors"
                    icon={Gem}
                    type="parent"
                    sidebarCollapsed={effectiveSidebarCollapsed}
                    onRequestExpand={onRequestExpand}
                    isExpanded={menuExpanded('ambassadors')}
                    onToggle={() => toggleMenu('ambassadors')}
                    registerLabel={registerItem}
                    userType={user?.type}
                    isMobile={isMobile}
                    onNavigate={handleNavigate}
                >
                    {(isAdmin || user?.features?.ambassador?.affiliation) && (
                        <NavigationItem.Child
                            id="affiliation"
                            label="Affiliation"
                            icon={Link2}
                            type="parent"
                            path="/dashboard/affiliation"
                        >
                            <NavigationItem.Child
                                id="referal"
                                label="Referral"
                                icon={Send}
                                status="wip"
                                path="/dashboard/affiliation/referal"
                            />
                            <NavigationItem.Child
                                id="marketing"
                                label="Marketing"
                                icon={Megaphone}
                                status="todo"
                                path="/dashboard/affiliation/marketing"
                            />
                            <NavigationItem.Child
                                id="affiliation-manager"
                                label="Affiliate Manager"
                                icon={FileSignature}
                                status="todo"
                                path="/dashboard/affiliation-manager"
                            />
                        </NavigationItem.Child>
                    )}

                    {(isAdmin || user?.features?.ambassador?.packCalculator) && (
                        <NavigationItem.Child
                            id="pack-calculator"
                            label="Packs"
                            icon={Package}
                            path="/dashboard/pack-calculator"
                        />
                    )}

                    <NavigationItem.Child id="crm-saas" label="CRM SaaS" icon={Layers} path="/dashboard/crm-saas" />
                </NavigationItem>
            )}

            {hasBizdevTools && (
                <NavigationItem
                    id="sales"
                    label="Biz Dev"
                    icon={LineChart}
                    type="parent"
                    sidebarCollapsed={effectiveSidebarCollapsed}
                    onRequestExpand={onRequestExpand}
                    isExpanded={menuExpanded('sales')}
                    onToggle={() => toggleMenu('sales')}
                    registerLabel={registerItem}
                    userType={user?.type}
                    isMobile={isMobile}
                    onNavigate={handleNavigate}
                >
                    {(user?.features?.bizdev?.networkReport || user?.features?.bizdev?.networkScanUpdates) && (
                        <NavigationItem.Child
                            id="crm-network-report"
                            label="Network Map"
                            icon={Network}
                            path="/dashboard/crm-network-report"
                        />
                    )}

                    {user?.features?.bizdev?.crm && (
                        <NavigationItem.Child id="crm-bizdev" label="CRM" icon={IdCard} path="/dashboard/crm-bizdev" />
                    )}

                    {/* Hidden for now — not deleted, easy to bring back later */}
                    {false && user?.features?.bizdev?.crm && (
                        <NavigationItem.Child
                            id="client-signals"
                            label="Client Signals"
                            icon={Radar}
                            path="/dashboard/client-signals"
                        />
                    )}

                    {user?.features?.bizdev?.campaigns ? (
                        <NavigationItem.Child
                            id="campaign"
                            label="Campaigns"
                            icon={Rss}
                            path="/dashboard/campaign"
                        />
                    ) : user?.isInTrial ? (
                        <ProLockedNavItem id="campaign" label="Campaigns" icon={Rss} feature="campaigns" onNavigate={handleNavigate} />
                    ) : null}

                    <NavigationItem.Child id="prospect" label="Import Leads" icon={Download} path="/dashboard/prospect" />

                    {isAdmin && user?.features?.bizdev?.crm && (
                        <NavigationItem.Child
                            id="csm-pricing"
                            label="Contract Calculator"
                            icon={Calculator}
                            path="/dashboard/csm-pricing"
                        />
                    )}
                </NavigationItem>
            )}

            {hasSourcingTools && (
                <NavigationItem
                    id="sourcing"
                    label="Sourcing"
                    icon={Radar}
                    type="parent"
                    sidebarCollapsed={effectiveSidebarCollapsed}
                    onRequestExpand={onRequestExpand}
                    isExpanded={menuExpanded('sourcing')}
                    onToggle={() => toggleMenu('sourcing')}
                    registerLabel={registerItem}
                    userType={user?.type}
                    isMobile={isMobile}
                    onNavigate={handleNavigate}
                >
                    {(isAdmin || user?.features?.sourcing?.activeRoles) && (
                        <NavigationItem.Child
                            id="active-roles"
                            label="Mandates"
                            icon={ClipboardList}
                            path="/dashboard/active-roles"
                        />
                    )}

                    {(isAdmin || user?.features?.sourcing?.activeRoles) && (
                        <NavigationItem.Child
                            id="ats-pipeline"
                            label="ATS Pipeline"
                            icon={Workflow}
                            path="/dashboard/pipeline"
                        />
                    )}

                    {(isAdmin || user?.features?.sourcing?.activeRoles) && (
                        <NavigationItem.Child
                            id="talent-pool"
                            label="Talent Pool"
                            icon={UsersRound}
                            path="/dashboard/talent-pool"
                        />
                    )}

                    {(isAdmin || user?.features?.sourcing?.campaigns) && (
                        <NavigationItem.Child
                            id="candidate-flow"
                            label="Campaigns"
                            icon={Send}
                            path="/dashboard/candidate-flow"
                        />
                    )}

                    <NavigationItem.Child
                        id="sourcing-search-people"
                        label="Import Candidates"
                        icon={ScanSearch}
                        path="/dashboard/sourcing/search"
                    />
                </NavigationItem>
            )}

            {isCSM && (
                <NavigationItem
                    id="cc-relationship"
                    label="CSM"
                    icon={LifeBuoy}
                    type="parent"
                    sidebarCollapsed={effectiveSidebarCollapsed}
                    onRequestExpand={onRequestExpand}
                    isExpanded={menuExpanded('cc-relationship')}
                    onToggle={() => toggleMenu('cc-relationship')}
                    registerLabel={registerItem}
                    userType={user?.type}
                    isMobile={isMobile}
                    onNavigate={handleNavigate}
                >
                    <NavigationItem.Child id="csm-start" label="Guide" icon={Compass} path="/csm" />
                    <NavigationItem.Child id="csm-missions" label="CSM" icon={Target} path="/dashboard/crm-mission" />
                </NavigationItem>
            )}

            <NavigationItem
                id="settings"
                label="Settings"
                icon={Settings2}
                type="parent"
                sidebarCollapsed={effectiveSidebarCollapsed}
                onRequestExpand={onRequestExpand}
                isExpanded={menuExpanded('settings')}
                onToggle={() => toggleMenu('settings')}
                registerLabel={registerItem}
                userType={user?.type}
                isMobile={isMobile}
                onNavigate={handleNavigate}
            >
                <NavigationItem.Child
                    id="account-details"
                    label="Account Details"
                    icon={UserCog}
                    path="/dashboard/account-details"
                />

                <NavigationItem.Child id="google" label="Integrations" icon={Plug} path="/dashboard/google" />
                {user?.roles?.includes('ADMIN') && (
                    <NavigationItem.Child id="admin-mcp" label="MCP" icon={Puzzle} path="/dashboard/mcp" />
                )}
            </NavigationItem>
            </div>

            <div
                className={`shrink-0 border-t border-slate-200 ${
                    effectiveSidebarCollapsed && !isMobile ? 'space-y-1 pt-2' : 'space-y-2 pt-3'
                }`}
            >
                {effectiveSidebarCollapsed && !isMobile ? (
                    <SidebarRailItem
                        label="Refer a Friend"
                        icon={<Sparkles size={18} strokeWidth={1.75} />}
                        href="/invite"
                        tone="accent"
                        dataTestId="nav-refer-friend-card"
                    />
                ) : (
                    <Link
                        to="/invite"
                        data-testid="nav-refer-friend-card"
                        className="group flex items-center gap-2.5 rounded-xl border border-orange-100 bg-orange-50/60 px-3 py-2.5 transition-all duration-200 hover:border-orange-200 hover:bg-orange-50"
                        onClick={handleNavigate}
                    >
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-primary text-white">
                            <Sparkles size={14} />
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-slate-700 group-hover:text-slate-900">
                            Refer a Friend
                        </span>
                    </Link>
                )}

                {onLogout &&
                    (effectiveSidebarCollapsed && !isMobile ? (
                        <SidebarRailItem
                            label="Log out"
                            icon={<LogOut size={18} strokeWidth={1.75} />}
                            onClick={onLogout}
                            tone="danger"
                            dataTestId="logout-button"
                        />
                    ) : (
                        <Button
                            type="button"
                            variant="red"
                            size="sm"
                            onClick={onLogout}
                            data-testid="logout-button"
                            aria-label="Log out"
                            title="Log out"
                            className="w-full"
                        >
                            <LogOut size={16} />
                            Log out
                        </Button>
                    ))}
            </div>
        </nav>
    );
};

export default NavigationMenu;
